if (!customElements.get('quick-add')) {
  let cachedDefaultWishlistId = null;

  customElements.define(
    'quick-add',
    class QuickAdd extends HTMLElement {
      constructor() {
        super();
        this.boundOpen = this.open.bind(this);
        this.boundColorSwatchChange = this.onColorSwatchChange.bind(this);
      }

      connectedCallback() {
        if (!this.isWishlistMode) {
          this.addEventListener('click', this.boundOpen);
        }
      }

      // Handles color swatch selection and modal replacement for linked products
      onColorSwatchChange(event) {
        // Ignore events during modal swap or from outside current modal
        if (this.hasAttribute('data-swapping-modal')) return;
        if (!this.modal) return;
        if (!this.modal.contains(event.target)) return;

        const input = event.target.closest('.radio__input[data-product-url]');
        if (!input) return;

        const linkedProductUrl = input.dataset.productUrl;
        if (!linkedProductUrl || linkedProductUrl === this.productUrl) return;

        const selectedOptions = this.getSelectedModalOptions(input);

        // Set flag to prevent variant-radios from updating during modal swap
        this.setAttribute('data-swapping-modal', 'true');
        this.replaceModalForLinkedProduct(linkedProductUrl, selectedOptions);
      }

      // Opens the quick-add modal by fetching the product section
      async open(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!this.productUrl) return;

        if (this.isWishlistMode) {
          this.closeSwymModals();
        }

        this._productUrl = this.productUrl;
        this.classList.add('loading');

        try {
          const separator = this.productUrl.includes('?') ? '&' : '?';
          const url = `${this.productUrl}${separator}section_id=quick-add`;
          const response = await fetch(url);
          const html = await response.text();
          this.render(html);
        } catch (error) {
        } finally {
          this.classList.remove('loading');
        }
      }

      // Parses fetched HTML, appends the modal to the DOM and binds close handlers
      render(html) {
        this.modal?.remove();

        const fragment = document.createElement('div');
        fragment.innerHTML = html;

        const content = fragment.querySelector('.quick-add') || fragment.firstElementChild;
        if (!content) return;

        if (this.isWishlistMode) {
          this.applyWishlistMode(content);
          this.closeSwymModals();
        }

        document.body.appendChild(content);
        this.bindColorSwatchListener(content);
        this.initMediaCarousel();

        requestAnimationFrame(() => {
          this.closeMiniCartDrawer();
          content.classList.add('quick-add--open');
          document.body.classList.add('quick-add-active');
          trapFocus(content);
        });

        this.bindCloseHandlers();
        if (this.isWishlistMode) {
          this.bindWishlistHandler();
        }
      }

      applyWishlistMode(modal) {
        modal.dataset.quickAddMode = 'wishlist';

        if (modal.querySelector('[data-add-to-wishlist]')) return;

        const addToCart = modal.querySelector('[data-add-to-cart]');
        if (!addToCart) return;

        const productId =
          modal.dataset.productId ||
          this.dataset.productId ||
          this.closest('product-card')?.dataset.productId;
        const productUrl =
          modal.dataset.productUrl || this.getFullProductUrl(this.productUrl);
        const wishlistText =
          window.variantStrings?.addToWishlist || 'Add to wishlist';
        const soldOutText =
          addToCart.dataset.soldOutText ||
          window.variantStrings?.soldOut ||
          'Sold out';
        const isDisabled = addToCart.hasAttribute('disabled');

        const wishlistButton = document.createElement('button');
        wishlistButton.type = 'button';
        wishlistButton.className = addToCart.className;
        wishlistButton.setAttribute('data-add-to-wishlist', '');
        if (addToCart.hasAttribute('data-validate-variant-selection')) {
          wishlistButton.setAttribute('data-validate-variant-selection', '');
        }
        wishlistButton.dataset.variantId = addToCart.dataset.variantId || '';
        wishlistButton.dataset.soldOutText = soldOutText;
        wishlistButton.dataset.productId = productId || '';
        wishlistButton.dataset.productUrl = productUrl;

        if (isDisabled) {
          wishlistButton.disabled = true;
        }

        wishlistButton.innerHTML = `<span class="button__text" data-add-to-wishlist-text>${
          isDisabled ? soldOutText : wishlistText
        }</span>`;

        addToCart.replaceWith(wishlistButton);
      }

      getFullProductUrl(productPath = '') {
        const path = String(productPath).split('?')[0];
        if (!path) return window.location.href;

        if (/^https?:\/\//i.test(path)) return path.split('?')[0];

        return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
      }

      closeSwymModals() {
        const selectors = [
          '#swym-custom-collections-modal-background',
          '.swym-custom-collections-modal-background',
          '#swym-custom-collections-modal',
          '.swym-custom-collections-modal',
        ];

        document.querySelectorAll(selectors.join(',')).forEach((element) => {
          element.style.display = 'none';
          element.setAttribute('aria-hidden', 'true');
          element.classList.remove('swym-show', 'active', 'swym-visible');
        });
      }

      bindColorSwatchListener(modal) {
        if (!modal) return;
        modal.addEventListener('change', this.boundColorSwatchChange);
      }

      // Fetches a fresh quick-add modal for the linked product and replaces current modal
      async replaceModalForLinkedProduct(productUrl, selectedOptions = []) {
        if (this.isReplacingModal) return;

        this.isReplacingModal = true;

        try {
          const separator = productUrl.includes('?') ? '&' : '?';
          const url = `${productUrl}${separator}section_id=quick-add`;
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error('Failed to fetch quick-add modal');
          }

          const html = await response.text();
          const fragment = document.createElement('div');
          fragment.innerHTML = html;

          const nextModal = fragment.querySelector('.quick-add');
          if (!nextModal) {
            throw new Error('No quick-add modal found in response');
          }

          this.restoreSelectedOptions(nextModal, selectedOptions);

          this.productUrl = productUrl;

          if (this.isWishlistMode) {
            this.applyWishlistMode(nextModal);
          }

          // Preserve open state during swap
          const wasOpen = this.modal?.classList.contains('quick-add--open');
          if (wasOpen) {
            nextModal.classList.add('quick-add--open');
          }

          this.modal.replaceWith(nextModal);
          this.bindColorSwatchListener(nextModal);

          // Use requestAnimationFrame to ensure the new modal is settled before reinit
          requestAnimationFrame(() => {
            this.initMediaCarousel();
            this.bindCloseHandlers();
            if (this.isWishlistMode) {
              this.bindWishlistHandler();
            }
            trapFocus(nextModal);
            // Clear swap flag to allow variant-radios to update again
            this.removeAttribute('data-swapping-modal');
          });
        } catch (error) {
          this.removeAttribute('data-swapping-modal');
        } finally {
          this.isReplacingModal = false;
        }
      }

      getSelectedModalOptions(changedInput) {
        const variantRadios = changedInput.closest('variant-radios');
        if (!variantRadios) return [];

        return Array.from(
          variantRadios.querySelectorAll('[data-variant-fieldset]'),
        )
          .map((fieldset) => {
            const input = fieldset.querySelector('[data-input]:checked');
            if (!input) return null;

            return {
              position: fieldset.dataset.optionPosition,
              value: input.value,
            };
          })
          .filter(Boolean);
      }

      // Carries customer selections into a linked-product modal before its
      // variant picker initialises. Unavailable values are left unselected.
      restoreSelectedOptions(modal, selectedOptions) {
        if (!modal || !selectedOptions.length) return;

        const fieldsets = Array.from(
          modal.querySelectorAll('[data-variant-fieldset]'),
        );

        selectedOptions.forEach((selectedOption) => {
          const fieldset = fieldsets.find(
            (item) =>
              !selectedOption.position ||
              item.dataset.optionPosition === selectedOption.position,
          );
          if (!fieldset) return;

          const matchingInput = Array.from(
            fieldset.querySelectorAll('[data-input]'),
          ).find((input) => input.value === selectedOption.value);

          if (matchingInput && !matchingInput.disabled) {
            matchingInput.checked = true;
          }
        });
      }

      // Closes the mini cart drawer when quick-add is opening
      closeMiniCartDrawer() {
        const miniCartDrawer = document.getElementById('mini-cart');
        if (!miniCartDrawer || !miniCartDrawer.classList.contains('active')) return;

        miniCartDrawer.dispatchEvent(new Event('drawer-toggle'));
      }

      // Attaches all close event listeners to the active modal
      bindCloseHandlers() {
        if (!this.modal) return;

        this.closeButton?.addEventListener('click', () => this.close());
        this.overlay?.addEventListener('click', () => this.close());
        this.modal.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') this.close();
        });

        this.addToCartButton?.addEventListener('click', (event) => {
          const addToCartButton = event.currentTarget;
          if (
            addToCartButton.classList.contains('disabled') ||
            addToCartButton.hasAttribute('disabled') ||
            addToCartButton.getAttribute('aria-disabled') === 'true'
          ) return;

          const variantPicker = this.modal?.querySelector(
            'variant-radios[data-require-selection]',
          );
          if (variantPicker && !variantPicker.hasCompleteSelection) return;
          if (!addToCartButton.getAttribute('data-variant-id')) return;

          setTimeout(() => this.close(), 300);
        });
      }

      bindWishlistHandler() {
        const wishlistButton = this.wishlistButton;
        if (!wishlistButton || wishlistButton.dataset.wishlistBound === 'true') return;

        wishlistButton.dataset.wishlistBound = 'true';
        wishlistButton.addEventListener('click', (event) => {
          event.preventDefault();
          if (wishlistButton.disabled) return;

          const variantPicker = this.modal?.querySelector(
            'variant-radios[data-allow-incomplete-submit]',
          );
          if (variantPicker?.reportMissingSelections?.()) return;

          const variantId = this.resolveWishlistVariantId();
          if (variantId) {
            wishlistButton.setAttribute('data-variant-id', String(variantId));
            wishlistButton.dataset.variantId = String(variantId);
          }

          wishlistButton.classList.add('loading');
          this.addToWishlist(wishlistButton);
        });
      }

      resolveWishlistVariantId() {
        const modal = this.modal;
        if (!modal) return null;

        const fallbackVariantId =
          modal.querySelector('[data-add-to-wishlist]')?.getAttribute('data-variant-id') || null;

        const variantRadios = modal.querySelector('variant-radios');
        if (!variantRadios) return fallbackVariantId;

        const fieldsets = Array.from(variantRadios.querySelectorAll('[data-variant-fieldset]'));
        const selectedByPosition = {};
        let hasMissingSelection = false;

        fieldsets.forEach((fieldset, index) => {
          const input = fieldset.querySelector('[data-input]:checked');

          if (!input) {
            hasMissingSelection = true;
            return;
          }

          const position = Number(fieldset.dataset.optionPosition) || index + 1;
          selectedByPosition[position] = input.value;
        });

        const selectedEntries = Object.entries(selectedByPosition);
        if (hasMissingSelection || !selectedEntries.length) return fallbackVariantId;

        let variants;
        try {
          variants = JSON.parse(
            variantRadios.querySelector('script[type="application/json"]').textContent,
          );
        } catch {
          return fallbackVariantId;
        }

        const matches = (variant) =>
          selectedEntries.every(([position, value]) => variant[`option${position}`] === value);

        const match =
          variants.find((variant) => matches(variant) && variant.available) ||
          variants.find(matches);

        return match?.id || fallbackVariantId;
      }

      addToWishlist(button) {
        const modal = this.modal;
        const variantId =
          this.resolveWishlistVariantId() || button.getAttribute('data-variant-id');
        const productId = modal?.dataset.productId || button.dataset.productId;
        const productUrl =
          modal?.dataset.productUrl ||
          button.dataset.productUrl ||
          this.getFullProductUrl(this.productUrl);

        if (!variantId || !productId || !productUrl) {
          button.classList.remove('loading');
          return;
        }

        const product = {
          epi: Number(variantId),
          empi: Number(productId),
          du: productUrl,
          source: 'collections-grid',
        };

        const onError = () => {
          button.classList.remove('loading');
        };

        const onSuccess = (addedItem) => {
          button.classList.remove('loading');
          this.notifySwymWishlistAdded(product, addedItem);
          document.dispatchEvent(
            new CustomEvent('wishlist:state-changed', {
              detail: { productId: product.empi, wishlisted: true },
            }),
          );
          setTimeout(() => this.close(), 200);
        };

        const addWithSwym = (swat) => {
          if (typeof swat.addToWishList === 'function') {
            swat.addToWishList(product, onSuccess, onError);
            return;
          }

          this.addToDefaultList(swat, product, onSuccess, onError);
        };

        if (window._swat) {
          addWithSwym(window._swat);
        } else {
          window.SwymCallbacks = window.SwymCallbacks || [];
          window.SwymCallbacks.push(addWithSwym);
        }
      }

      addToDefaultList(swat, product, onSuccess, onError) {
        const addWithListId = (listId) => {
          if (!listId) {
            onError();
            return;
          }

          swat.addToList(listId, product, onSuccess, onError);
        };

        if (cachedDefaultWishlistId) {
          addWithListId(cachedDefaultWishlistId);
          return;
        }

        swat.fetchLists({
          callbackFn: (lists) => {
            cachedDefaultWishlistId = lists?.[0]?.lid || null;
            addWithListId(cachedDefaultWishlistId);
          },
          errorFn: onError,
        });
      }

      notifySwymWishlistAdded(product, addedItem) {
        const swat = window._swat;
        if (!swat) return;

        const payload = { ...product, ...(addedItem || {}) };
        const eventName =
          swat.JSEvents?.addedToWishlist || 'sw:addedtowishlist';

        swat.evtLayer?.dispatchEvent(
          new CustomEvent(eventName, {
            detail: { d: payload },
          }),
        );

        if (typeof swat.updateWishlistCount === 'function') {
          swat.updateWishlistCount();
        }

        const successMessage =
          window.variantStrings?.addedToWishlist ||
          'Added to wishlist';

        swat.ui?.showSuccessNotification?.({ message: successMessage });
      }

      // Closes the modal with a transition and removes it from the DOM
      close() {
        if (!this.modal) {
          this.cleanupQuickAddArtifacts();
          return;
        }

        const modal = this.modal;
        modal.classList.remove('quick-add--open');
        document.body.classList.remove('quick-add-active');
        removeTrapFocus();

        // Remove backdrop immediately so the page is never left greyed out.
        modal.querySelector('.quick-add__overlay')?.remove();

        if (this.swiperInstance) {
          this.swiperInstance.destroy();
          this.swiperInstance = undefined;
        }

        const finalizeClose = () => {
          if (document.body.contains(modal)) modal.remove();
          this.cleanupQuickAddArtifacts();
        };

        modal.addEventListener('transitionend', finalizeClose, { once: true });
        setTimeout(() => {
          if (document.body.contains(modal)) finalizeClose();
        }, 500);
      }

      cleanupQuickAddArtifacts() {
        document.body.classList.remove('quick-add-active');

        document.querySelectorAll('.quick-add').forEach((quickAddModal) => {
          if (!quickAddModal.classList.contains('quick-add--open')) {
            quickAddModal.remove();
          }
        });

        document.querySelectorAll('.quick-add__overlay').forEach((overlay) => {
          if (!overlay.closest('.quick-add')) {
            overlay.remove();
          }
        });
      }

      // Initialises a Swiper carousel on the quick-add media (desktop only)
      initMediaCarousel() {
        if (!this.mediaCarousel) return;

        // Remove hidden class to allow Swiper to calculate dimensions
        this.mediaCarousel.classList.remove('hidden');

        this.swiperInstance = new Swiper(this.mediaCarousel, {
          slidesPerView: 1,
          enabled: false,
          navigation: {
            nextEl: this.mediaCarousel.querySelector('.swiper-button-next'),
            prevEl: this.mediaCarousel.querySelector('.swiper-button-prev'),
          },
          breakpoints: {
            990: {
              enabled: true,
            },
          },
        });

        // Recalculate after modal becomes visible
        requestAnimationFrame(() => {
          this.swiperInstance.update();
        });
      }

      get productUrl() {
        return this._productUrl || this.dataset.productUrl;
      }

      set productUrl(url) {
        this._productUrl = url;
      }

      get modal() {
        return document.querySelector('.quick-add');
      }

      get overlay() {
        return this.modal?.querySelector('.quick-add__overlay');
      }

      get closeButton() {
        return this.modal?.querySelector('[data-quick-add-close]');
      }

      get addToCartButton() {
        return this.modal?.querySelector('add-to-cart');
      }

      get wishlistButton() {
        return this.modal?.querySelector('[data-add-to-wishlist]');
      }

      get isWishlistMode() {
        return this.dataset.mode === 'wishlist';
      }

      get mediaCarousel() {
        return this.modal?.querySelector('.quick-add__media-carousel');
      }
    }
  );

}
